import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import NotFoundPage from 'shared/components/NotFoundPage'
import Interface from 'features/onboarding/pages/LoginForm'
import ResetPassword from 'features/onboarding/pages/ResetPassword'
import Dashboard from 'features/dashboard/pages/Dashboard'
import { AppDialogProvider } from "shared/context/AppDialogContext";
import { TeamProvider, useTeam } from "shared/context/teamContext";
import { AnnotationProvider } from "features/agent-analytics/annotations";
import { SubscriptionProvider } from "shared/context/subscriptionContext";
import { PreferencesProvider } from "shared/context/preferencesContext";
import {
  resolveSettingsSection,
  SETTINGS_SECTION_HEADING,
} from "features/settings/settingsNavConfig";
import AnalyticsLayout from "features/analytics/studio/pages/AnalyticsLayout";
import BoardViewPage from "features/analytics/studio/pages/BoardViewPage";
import PublicBoardPage from "features/analytics/studio/pages/PublicBoardPage";
import InsightsEditorPage from "features/analytics/studio/pages/InsightsEditorPage";
import FlowEditorPage from "features/analytics/studio/pages/FlowEditorPage";
import ReportsPage from "features/analytics/studio/pages/ReportsPage";
import SubscriptionProtectedRoute from "shared/components/subscription/SubscriptionProtectedRoute";
import ErrorBoundary from 'shared/components/ErrorBoundary';
import WorkspaceSetupGate from 'shared/components/WorkspaceSetupGate';
import NewProjectPage from 'features/onboarding/pages/NewProjectPage';
import InstallProjectPage from 'features/onboarding/pages/InstallProjectPage';
import InstallDeployPage from 'features/onboarding/pages/InstallDeployPage';
import ConnectMCP from 'features/connect/ConnectMCP';
import ConnectCLI from 'features/connect/ConnectCLI';
import { AnalyticsCacheProvider } from 'shared/context/AnalyticsCacheContext';
import 'shared/styles/animations.css';
import 'shared/styles/resizeObserverFix.css';
import suppressResizeObserverErrors from 'shared/utils/resizeObserverFix';
import { LEGACY_STUDIO_UI } from 'shared/config/featureFlags';
import PublicDashboardPage from 'features/dashboards/pages/PublicDashboardPage';

// Apply ResizeObserver error suppression globally
suppressResizeObserverErrors();

// Title updater component - this doesn't need access to Team context
const TitleUpdater = () => {
  const location = useLocation();

  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean); // removes empty strings

    // Settings owns every config page now (Console and Configuration folded in),
    // so derive the title from its section map rather than the raw last segment.
    const settingsIdx = pathSegments.indexOf('settings');
    if (settingsIdx >= 0) {
      const heading = SETTINGS_SECTION_HEADING[resolveSettingsSection(location.pathname)];
      document.title = heading ? `Settings: ${heading}` : 'Settings';
      return;
    }

    // A dashboard page titles itself with the dashboard's name.
    const reportsIdx = pathSegments.indexOf('reports');
    if (reportsIdx >= 0 && pathSegments[reportsIdx + 1] && pathSegments[reportsIdx - 1] !== 'analytics') return;
    if (pathSegments[1] === 'overview') return;

    const agentAnalyticsIdx = pathSegments.indexOf('agent-analytics');
    if (agentAnalyticsIdx >= 0) {
      const sub = pathSegments[agentAnalyticsIdx + 1] || 'overview';
      const titles = {
        overview: 'Agent Analytics: Overview',
        runs: 'Agent Analytics: Runs',
        clusters: 'Agent Analytics: Clusters',
        conversations: 'Traces: Conversations',
        traces: 'Traces: Spans',
        issues: 'Issues',
      };
      document.title = titles[sub] || 'Agent Analytics';
      return;
    }

    const titleMap = {
      dashboard: 'Home',
      analytics: 'Analytics',
      reports: 'Reports',
      home: 'Home',
      customize: 'Customize',
      chat: 'Lucid',
      reports: 'Reports',
      boards: 'Reports',
      editor: 'Report Editor',
      settings: 'Settings',
      general: 'Settings',
      project: 'Settings',
      usage: 'Settings',
      members: 'Settings',
      billing: 'Settings',
      teamsSection: 'Teams',
      campaigns: 'Campaigns',
      activity: 'Product Analytics',
      sessions: 'Activity',
      integrations: 'Integrations',
      inbox: 'Inbox',
      'flow': 'Feature Flow',
      login: 'Login',
      signup: 'Sign Up',
      'reset-password': 'Reset Password',
      'new-project': 'New project',
      overview: 'Overview',
      alerts: 'Alerts',
      agents: 'Agents',
      evals: 'Evals',
      datasets: 'Datasets',
      prompts: 'Prompts',
      playground: 'Playground',
      dashboards: 'Dashboards',
      install: 'Install',
      configuration: 'Configuration',
      events: 'Events',
      users: 'Users',
    };

    // The nearest known segment names the page: /team/alerts/123 is Alerts.
    const known = [...pathSegments].reverse().find((seg) => titleMap[String(seg).toLowerCase()]);
    const pageTitle = known ? titleMap[String(known).toLowerCase()] : (pathSegments.length ? 'Trodo' : 'Login');
    document.title = `${pageTitle}`;
  }, [location]);

  return null;
};

// Favicon updater component - switches favicon based on dark/light mode
const FaviconUpdater = () => {
  useEffect(() => {
    const updateFavicon = () => {
      // Check if user prefers dark mode
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      
      // Find existing favicon link or create a new one
      let faviconLink = document.querySelector("link[rel~='icon']");
      if (!faviconLink) {
        faviconLink = document.createElement('link');
        faviconLink.rel = 'icon';
        document.head.appendChild(faviconLink);
      }
      
      // Get the public URL (for React apps, this is usually empty string or process.env.PUBLIC_URL)
      const publicUrl = process.env.PUBLIC_URL || '';
      
      // Set the appropriate favicon based on theme
      // Supports both .ico and .png formats - try .ico first, fallback to .png
      const faviconName = prefersDark ? 'favicon-dark' : 'favicon-light';
      faviconLink.href = `${publicUrl}/${faviconName}.ico`;
    };

    // Initial update
    updateFavicon();

    // Listen for theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => updateFavicon();
    
    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } 
    // Fallback for older browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  return null;
};

/** Old onboarding URLs redirect to home reports */
const LegacyOnboardingRedirect = () => {
  const { team } = useParams();
  if (!team) return <Navigate to="/login" replace />;
  return <Navigate to={`/${encodeURIComponent(decodeURIComponent(team))}/analytics/reports`} replace />;
};

/**
 * The browser SDK's pageview side (Events, Sessions, the heatmap and the old
 * "offchain" alias) was retired on 28 Oct 2026. Every old link to it lands on
 * Users, the part of Activity that stays (identities and groups come from the
 * backend SDK).
 */
/** Experiments is retired: its jobs are dataset agents now. Old links land on Datasets. */
const RetiredExperimentsRedirect = () => {
  const { team } = useParams();
  if (!team) return <Navigate to="/login" replace />;
  return <Navigate to={`/${encodeURIComponent(decodeURIComponent(team))}/datasets`} replace />;
};

const RetiredActivityRedirect = () => {
  const { team } = useParams();
  if (!team) return <Navigate to="/login" replace />;
  return <Navigate to={`/${encodeURIComponent(decodeURIComponent(team))}/activity/users`} replace />;
};

const AgentAnalyticsRootRedirect = () => {
  const { team } = useParams();
  if (!team) {
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={`/${encodeURIComponent(team)}/overview`} replace />;
};

/** Legacy /:team/trace/* or /:team/agent-analytics/conversations → runs */
const LegacyTraceToRunsRedirect = () => {
  const { team } = useParams();
  if (!team) {
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={`/${encodeURIComponent(team)}/agent-analytics/runs`} replace />;
};

const DashboardsRenamedRedirect = () => {
  const { team } = useParams();
  const location = useLocation();
  const rest = location.pathname.split('/').slice(3).join('/');
  const to = rest === 'overview' ? 'overview' : `reports${rest ? `/${rest}` : ''}`;
  return <Navigate to={`/${encodeURIComponent(decodeURIComponent(team))}/${to}${location.search}`} replace />;
};

const LegacyReportsRedirect = () => {
  const { team } = useParams();
  if (!team) {
    return <Navigate to="/login" replace />;
  }
  const to = LEGACY_STUDIO_UI ? 'analytics/reports' : 'reports';
  return <Navigate to={`/${encodeURIComponent(team)}/${to}`} replace />;
};

/**
 * The studio is hidden (TRO-466): every /:team/analytics/* bookmark lands on
 * Dashboards.
 */
const StudioHiddenRedirect = () => {
  const { team } = useParams();
  if (!team) return <Navigate to="/login" replace />;
  return <Navigate to={`/${encodeURIComponent(decodeURIComponent(team))}/reports`} replace />;
};

/**
 * Home is the chat. The old Home workspace (widgets, customize, its editors)
 * was deleted with Dashboards v2 (TRO-466); every bookmark into it lands on
 * the chat.
 */
const LegacyAnalyticsHomeRedirect = () => {
  const { team } = useParams();
  if (!team) return <Navigate to="/login" replace />;
  return <Navigate to={`/${encodeURIComponent(decodeURIComponent(team))}/chat`} replace />;
};

function slackSettingsPath(search) {
  const params = new URLSearchParams(search || '');
  if (params.has('slack') || params.has('org_slack') || params.get('integration') === 'slack') {
    params.delete('integration');
    const qs = params.toString();
    return `/settings/org-slack${qs ? `?${qs}` : ''}`;
  }
  return `/settings/integrations${search || ''}`;
}

/**
 * Legacy /:team/managewebsites and the retired standalone /:team/integrations
 * → Settings > Project > Integrations. Slack OAuth returns go to Organization Slack.
 */
const LegacyIntegrationsPathRedirect = () => {
  const { team } = useParams();
  const { search } = useLocation();
  if (!team) {
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={`/${encodeURIComponent(team)}${slackSettingsPath(search)}`} replace />;
};

/**
 * A settings link with no team in it — the personal Slack callback's own
 * fallback is /settings/connected-accounts — went to the catch-all and landed
 * on Ask, so the page that would have shown the error never opened. Send it
 * into the last-used team's settings, keeping the sub-path and the query.
 */
const BareSettingsRedirect = () => {
  const location = useLocation();
  const teamName = localStorage.getItem('selectedTeam');
  const rest = location.pathname.split('/settings')[1] || '';
  const search = location.search || '';
  if (!teamName) return <Navigate to={`/login${search}`} replace />;
  return <Navigate to={`/${encodeURIComponent(teamName)}/settings${rest}${search}`} replace />;
};

/** Slack/Linear OAuth used to fall back to /integrations (no team). Send to Settings. */
const BareIntegrationsRedirect = () => {
  const { search } = useLocation();
  const teamName = localStorage.getItem('selectedTeam');
  if (!teamName) return <Navigate to={`/login${search || ''}`} replace />;
  return <Navigate to={`/${encodeURIComponent(teamName)}${slackSettingsPath(search)}`} replace />;
};

/**
 * /configuration/* was folded into Settings — Model prices and Issue thresholds
 * now live under Project there. Preserve the sub-path (including the
 * model-prices/:model drill-down) so old links land on the same page.
 */
const LegacyConfigurationRedirect = () => {
  const { team } = useParams();
  const location = useLocation();
  if (!team) {
    return <Navigate to="/login" replace />;
  }
  const rest = location.pathname.split('/configuration')[1] || '';
  const target = rest && rest !== '/' ? rest : '/model-prices';
  return <Navigate to={`/${encodeURIComponent(team)}/settings${target}`} replace />;
};

/**
 * /console/* was folded into Settings → Project (Events / Cohorts / Campaigns).
 * Preserve the sub-path so old links land on the same page.
 */
const LegacyConsoleRedirect = () => {
  const { team } = useParams();
  const location = useLocation();
  if (!team) {
    return <Navigate to="/login" replace />;
  }
  const rest = location.pathname.split('/console')[1] || '';
  const target = rest && rest !== '/' ? rest : '/events';
  return <Navigate to={`/${encodeURIComponent(team)}/settings${target}`} replace />;
};

/** Legacy standalone /:team/campaigns → Settings → Project → Campaigns. */
const LegacyCampaignsRedirect = () => {
  const { team } = useParams();
  if (!team) {
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={`/${encodeURIComponent(team)}/settings/campaigns`} replace />;
};

/** Legacy /cipher → the Events page, which now lives under Settings. */
const LegacyCipherToConsoleRedirect = () => {
  const { team } = useParams();
  if (!team) {
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={`/${encodeURIComponent(team)}/settings/events`} replace />;
};

const LegacyPublicDashboardRetired = () => (
  <div
    className="min-h-screen flex items-center justify-center p-8"
    style={{ fontFamily: 'Inter, sans-serif', color: 'var(--text-primary)' }}
  >
    <div className="text-center max-w-md">
      <h1 className="text-xl font-semibold mb-2">Link no longer available</h1>
      <p className="text-sm text-ink-secondary">
        Shared dashboards from the legacy product have been removed. Sign in to use Reports and boards in the app.
      </p>
    </div>
  </div>
);

// Redirect /dashboard to team Chat for the selected team (name kept for route clarity)
const DashboardToHomeRedirect = () => {
  const { selectedTeam, isLoading } = useTeam();
  if (isLoading) return null;
  const teamSlug = selectedTeam?.name || selectedTeam?._id || localStorage.getItem('selectedTeam');
  if (!teamSlug) {
    return <Navigate to="/login" replace />;
  }
  const encoded = encodeURIComponent(teamSlug);
  return <Navigate to={`/${encoded}/chat`} replace />;
};

// Team data refresher is no longer needed - teamContext handles caching and API calls efficiently

// Where to send an already-authenticated user who lands on a public/auth route.
// Presence-only (mirrors ProtectedRoute): if a token exists, route into the app
// and let the normal 401 -> refresh flow handle an expired-but-refreshable token.
const resolveAuthedLanding = (location) => {
  const params = new URLSearchParams(location?.search || '');
  const next = params.get('next');
  // Only honor same-origin relative paths to avoid open-redirects.
  if (next && next.startsWith('/') && !next.startsWith('//')) return next;
  const teamName = localStorage.getItem('selectedTeam');
  if (teamName) return `/${encodeURIComponent(teamName)}/chat`;
  return '/new-project';
};

// Root: drop signed-in users back into the app (their last team), else to login.
const RootRedirect = () => {
  const location = useLocation();
  const token = localStorage.getItem('accessToken');
  return <Navigate to={token ? resolveAuthedLanding(location) : '/login'} replace />;
};

// Login page guard: a returning user with a live session should NOT be shown the
// login form. Opening the app in a new tab/window lands on `/` -> `/login`; this
// sends them straight back to their last team instead of forcing a re-login.
const PublicAuthRoute = ({ children }) => {
  const token = localStorage.getItem('accessToken');
  const location = useLocation();
  if (token) return <Navigate to={resolveAuthedLanding(location)} replace />;
  return children;
};

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('accessToken');
  const location = useLocation();
  if (!token) {
    // Preserve the originally requested path (incl. query + hash) so the
    // login flow can return the user there. Critical for OAuth consent
    // entry points like /connect/mcp?flow=… where Claude / Cursor sent
    // the user with state we cannot afford to lose.
    const here = `${location.pathname}${location.search}${location.hash}`;
    if (here && here !== '/' && here !== '/login') {
      return <Navigate to={`/login?next=${encodeURIComponent(here)}`} replace />;
    }
    return <Navigate to="/login" replace />;
  }
  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppDialogProvider>
        <TeamProvider>
          <AnnotationProvider>
          <SubscriptionProvider>
            <PreferencesProvider>
              <AnalyticsCacheProvider>
                  <TitleUpdater />
                  <FaviconUpdater />
                  <WorkspaceSetupGate />
                  {/* No ActivationGate here any more. It used to redirect an
                      un-instrumented team to a full-screen install wall, which
                      took the sidebar with it: so those teams could not reach
                      Settings to invite a developer or upgrade to Pro until
                      after tracing was live. The shell now always renders and
                      only the page content is swapped; see useTeamActivation
                      and InstallPanel. */}
                <Routes>
              {/* Root: into the app if signed in, else login */}
              <Route path="/" element={<RootRedirect />} />

              {/* Public routes */}
              {/* /login is guarded: a live session skips the form and lands back on the last team.
                  /signup is intentionally NOT guarded: the OTP verification step sets an
                  accessToken then redirects to /signup?email=…, which the guard would break. */}
              <Route path="/login" element={<PublicAuthRoute><Interface /></PublicAuthRoute>} />
              <Route path="/signup" element={<Interface />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/dashboard/public/:token" element={<LegacyPublicDashboardRetired />} />
              <Route path="/analytics/public/:token" element={<PublicBoardPage />} />
              <Route path="/public/reports/:token" element={<PublicDashboardPage />} />
              {/* Links shared before the rename keep working. */}
              <Route path="/public/dashboards/:token" element={<PublicDashboardPage />} />
              
              {/* Protected routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardToHomeRedirect />
                </ProtectedRoute>
              } />
              {/* MCP OAuth consent screen: Slice M2. JWT-protected. */}
              <Route path="/connect/mcp" element={
                <ProtectedRoute>
                  <ConnectMCP />
                </ProtectedRoute>
              } />
              {/* `trodo login`: the terminal's code, approved here. JWT-protected. */}
              <Route path="/cli/connect" element={
                <ProtectedRoute>
                  <ConnectCLI />
                </ProtectedRoute>
              } />
              <Route path="/:team/custom-dashboard/*" element={
                <ProtectedRoute>
                  <LegacyReportsRedirect />
                </ProtectedRoute>
              } />
              <Route path="/:team/offchain" element={
                <ProtectedRoute>
                  <RetiredActivityRedirect />
                </ProtectedRoute>
              } />
              <Route path="/:team/custom-events" element={
                <ProtectedRoute>
                  <LegacyReportsRedirect />
                </ProtectedRoute>
              } />
              {/* Campaigns moved under Settings → Project. */}
              <Route path="/:team/campaigns" element={
                <ProtectedRoute>
                  <LegacyCampaignsRedirect />
                </ProtectedRoute>
              } />
              <Route path="/:team/activity" element={<RetiredActivityRedirect />} />
              {['events', 'sessions', 'offchain', 'onchain'].map((sub) => (
                <Route key={sub} path={`/:team/activity/${sub}`} element={<RetiredActivityRedirect />} />
              ))}
              <Route path="/:team/activity/users/:distinctId" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="activity">
                    <Dashboard />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/activity/groups/:groupKey/:groupId" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="activity">
                    <Dashboard />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/activity/heatmap" element={
                <ProtectedRoute>
                  <RetiredActivityRedirect />
                </ProtectedRoute>
              } />
              <Route path="/:team/activity/:subpage" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="activity">
                    <Dashboard />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              {/* Conversations is a real page again: the coarsest of the three
                  Traces grains. It used to redirect to runs. */}
              <Route path="/:team/agent-analytics/conversations" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="activity">
                    <Dashboard />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/agent-analytics" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="activity">
                    <AgentAnalyticsRootRedirect />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/agent-analytics/:subpage" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="activity">
                    <Dashboard />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/trace" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="activity">
                    <LegacyTraceToRunsRedirect />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/trace/:subpage" element={
                <ProtectedRoute>
                  <SubscriptionProtectedRoute requiredPage="activity">
                    <LegacyTraceToRunsRedirect />
                  </SubscriptionProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/:team/events" element={<RetiredActivityRedirect />} />

              <Route path="/:team/managewebsites" element={
                <ProtectedRoute>
                  <LegacyIntegrationsPathRedirect />
                </ProtectedRoute>
              } />
              {/* Integrations moved into Settings > Project. The old route's
                  SubscriptionProtectedRoute is not carried over on purpose:
                  'integrations' is in accessiblePages for free, pro AND
                  enterprise, so the gate never rejected anyone. */}
              <Route path="/integrations" element={
                <ProtectedRoute>
                  <BareIntegrationsRedirect />
                </ProtectedRoute>
              } />
              <Route path="/settings/*" element={
                <ProtectedRoute>
                  <BareSettingsRedirect />
                </ProtectedRoute>
              } />
              <Route path="/:team/integrations" element={
                <ProtectedRoute>
                  <LegacyIntegrationsPathRedirect />
                </ProtectedRoute>
              } />
              <Route path="/:team/inbox" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              {/* Agents (workflows) — internal sub-routing handled by AgentsPage */}
              <Route path="/:team/agents/*" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              {/* Alerts — "tell me when X happens", the detector half of what
                  used to be buried in the workflow editor. */}
              <Route path="/:team/alerts/*" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              {/* Playground: internal sub-routing handled by PlaygroundPage */}
              <Route path="/:team/playground/*" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              {/* Prompts: internal sub-routing handled by PromptsPage */}
              <Route path="/:team/prompts/*" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              {/* Datasets: internal sub-routing handled by DatasetsPage */}
              <Route path="/:team/datasets/*" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              {/* Experiments retired (Nov 2026): comparing models and labeling are dataset agents. */}
              <Route path="/:team/experiments/*" element={<RetiredExperimentsRedirect />} />
              <Route path="/:team/cq-intelligence" element={
                <ProtectedRoute>
                  <LegacyReportsRedirect />
                </ProtectedRoute>
              } />
              <Route path="/:team/cipher" element={
                <ProtectedRoute>
                  <LegacyCipherToConsoleRedirect />
                </ProtectedRoute>
              } />
              {/* Configuration was folded into Settings: redirect old links. */}
              <Route path="/:team/configuration/*" element={
                <ProtectedRoute>
                  <LegacyConfigurationRedirect />
                </ProtectedRoute>
              } />
              <Route path="/:team/configuration" element={
                <ProtectedRoute>
                  <LegacyConfigurationRedirect />
                </ProtectedRoute>
              } />
              {/* Console was folded into Settings → Project: redirect old links. */}
              <Route path="/:team/console/*" element={
                <ProtectedRoute>
                  <LegacyConsoleRedirect />
                </ProtectedRoute>
              } />
              <Route path="/:team/console" element={
                <ProtectedRoute>
                  <LegacyConsoleRedirect />
                </ProtectedRoute>
              } />
              <Route path="/:team/home/*" element={
                <ProtectedRoute>
                  <LegacyAnalyticsHomeRedirect />
                </ProtectedRoute>
              } />
              <Route path="/:team/home" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/:team/chat" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              {/* Protected settings routes - all render Dashboard which shows Settings with secondary nav */}
              <Route path="/:team/settings" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              {/* Every settings section (incl. model-prices/:model, mcp, and
                  legacy segments) resolves inside Settings.js. */}
              <Route path="/:team/settings/*" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/:team/user-settings" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/:team/onboarding" element={
                <ProtectedRoute>
                  <LegacyOnboardingRedirect />
                </ProtectedRoute>
              } />
              {/* Onboarding install wall: shown until the team ingests its
                  first run. Not wrapped by ActivationGate (which excludes
                  /:team/install*), so an unactivated team can actually reach it. */}
              <Route path="/:team/install" element={
                <ProtectedRoute>
                  <InstallProjectPage />
                </ProtectedRoute>
              } />
              <Route path="/:team/install/deploy" element={
                <ProtectedRoute>
                  <InstallDeployPage />
                </ProtectedRoute>
              } />
              <Route path="/new-project" element={
                <ProtectedRoute>
                  <NewProjectPage />
                </ProtectedRoute>
              } />
              <Route path="/:team/new-project" element={
                <ProtectedRoute>
                  <NewProjectPage />
                </ProtectedRoute>
              } />
              {/* Reports (TRO-466..472): internal sub-routing in DashboardsPage.
                  The team's default board is the Overview, at its own address. */}
              <Route path="/:team/reports/*" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/:team/overview/*" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              {/* Dashboards was renamed Reports: old links land in the same place. */}
              <Route path="/:team/dashboards/*" element={<DashboardsRenamedRedirect />} />
              {/* Analytics Studio (reports, boards, insights) — hidden unless
                  built with REACT_APP_LEGACY_STUDIO=on. */}
              {LEGACY_STUDIO_UI ? (
                <Route path="/:team/analytics" element={
                  <ProtectedRoute>
                    <AnalyticsLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Navigate to="reports" replace />} />
                  <Route path="reports" element={<ReportsPage />} />
                  <Route path="home" element={<LegacyAnalyticsHomeRedirect />} />
                  <Route path="home/editor" element={<LegacyAnalyticsHomeRedirect />} />
                  <Route path="home/flow" element={<LegacyAnalyticsHomeRedirect />} />
                  <Route path="boards" element={<Navigate to="reports" replace />} />
                  <Route path="boards/:boardId" element={<BoardViewPage />} />
                  <Route path="boards/:boardId/editor" element={<InsightsEditorPage />} />
                  <Route path="flow" element={<FlowEditorPage />} />
                  <Route path="flow/:boardId" element={<FlowEditorPage />} />
                </Route>
              ) : (
                <Route path="/:team/analytics/*" element={
                  <ProtectedRoute>
                    <StudioHiddenRedirect />
                  </ProtectedRoute>
                } />
              )}

                {/* Last, so it only catches what nothing else did. */}
                <Route path="*" element={<NotFoundPage />} />
                </Routes>
                  </AnalyticsCacheProvider>
            </PreferencesProvider>
              </SubscriptionProvider>
          </AnnotationProvider>
            </TeamProvider>
        </AppDialogProvider>
          </BrowserRouter>
    </ErrorBoundary>
  )
}

export default App
